import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError, MONTHS_FR } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList,
} from "recharts";
import { CalendarDays, Users2, TrendingUp, Building2, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const AMBER = "#F59E0B";
const PALETTE = ["#F59E0B", "#38BDF8", "#10B981", "#8B5CF6", "#EF4444", "#EC4899", "#14B8A6", "#F97316"];

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
        <Icon className="h-4 w-4 text-primary" /> {label}
      </div>
      <div className="font-display text-3xl font-extrabold mt-2 tabular-nums">{value}</div>
    </div>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={testid}>
      <h3 className="font-display font-bold mb-4">{title}</h3>
      {children}
    </div>
  );
}

const tooltipStyle = {
  contentStyle: { background: "#111827", border: "1px solid #2D3748", borderRadius: 8, color: "#fff" },
  itemStyle: { color: "#ffffff" },
  labelStyle: { color: "#ffffff" },
  cursor: { fill: "rgba(245,158,11,0.08)" },
};

export default function StatsPage() {
  const [periods, setPeriods] = useState({ years: [] });
  const [year, setYear] = useState("all");
  const [data, setData] = useState(null);
  const [ville, setVille] = useState(null);
  const [villeList, setVilleList] = useState([]);
  const [villeSearch, setVilleSearch] = useState("");

  const openDrill = async (dim, value, title) => {
    if (value === null || value === undefined || value === "") return;
    setVille(title); setVilleSearch(""); setVilleList([]);
    try {
      const params = { dim, value };
      if (year !== "all") params.year = Number(year);
      const { data } = await api.get("/stats-drill", { params });
      setVilleList(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    api.get("/periods").then(({ data }) => setPeriods(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const params = year === "all" ? {} : { year: Number(year) };
      const { data } = await api.get("/stats", { params });
      setData(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-muted-foreground">Chargement...</div>;

  const monthData = data.by_month.filter((m) => m.value > 0).map((m) => ({ name: MONTHS_FR[m.month].slice(0, 3), value: m.value, month: m.month }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Statistiques</h1>
          <p className="text-sm text-muted-foreground mt-1">Vue d'ensemble de l'activité de production</p>
        </div>
        <Select value={String(year)} onValueChange={setYear}>
          <SelectTrigger className="w-[160px]" data-testid="stats-year-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes années</SelectItem>
            {periods.years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={CalendarDays} label="Concerts" value={data.total_concerts} />
        <Kpi icon={TrendingUp} label="Jauge cumulée" value={data.total_jauge.toLocaleString("fr-FR")} />
        <Kpi icon={Building2} label="Jauge moyenne" value={data.avg_jauge.toLocaleString("fr-FR")} />
        <Kpi icon={Users2} label="Régisseurs" value={data.by_intermittent.length} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Concerts par mois · cliquez une barre" testid="chart-by-month">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" vertical={false} />
              <XAxis dataKey="name" stroke="#6B7280" fontSize={12} />
              <YAxis stroke="#6B7280" fontSize={12} allowDecimals={false} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="value" fill={AMBER} radius={[4, 4, 0, 0]} cursor="pointer" onClick={(d) => openDrill("month", d && d.month, `Concerts en ${MONTHS_FR[d && d.month]}${year !== "all" ? " " + year : ""}`)} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Concerts par ville · cliquez une barre" testid="chart-by-ville">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.by_ville} layout="vertical" margin={{ left: 20, right: 28 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" horizontal={false} />
              <XAxis type="number" tick={{ fill: "#E5E7EB", fontSize: 12 }} allowDecimals={false} />
              <YAxis type="category" dataKey="label" tick={{ fill: "#E5E7EB", fontSize: 11 }} width={90} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} cursor="pointer" onClick={(d) => openDrill("ville", d && d.label, `Concerts à ${d && d.label}`)}>
                {data.by_ville.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                <LabelList dataKey="value" position="right" fill="#ffffff" fontSize={12} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Top salles · cliquez une barre" testid="chart-by-salle">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.by_salle} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" horizontal={false} />
              <XAxis type="number" stroke="#6B7280" fontSize={12} allowDecimals={false} />
              <YAxis type="category" dataKey="label" stroke="#6B7280" fontSize={11} width={90} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="value" fill="#38BDF8" radius={[0, 4, 4, 0]} cursor="pointer" onClick={(d) => openDrill("salle", d && d.label, `Concerts — ${d && d.label}`)} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Concerts par régisseur · cliquez une barre" testid="chart-by-intermittent">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.by_intermittent.filter((x) => x.value > 0)} margin={{ bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" vertical={false} />
              <XAxis dataKey="name" stroke="#6B7280" fontSize={11} interval={0} angle={-35} textAnchor="end" height={60} />
              <YAxis stroke="#6B7280" fontSize={12} allowDecimals={false} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="value" fill="#10B981" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(d) => openDrill("regisseur", d && d.name, `Concerts de ${d && d.name}`)} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <Dialog open={Boolean(ville)} onOpenChange={(o) => !o && setVille(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="ville-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{ville}</DialogTitle>
            <DialogDescription>{villeList.length} concert(s){year !== "all" ? ` en ${year}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={villeSearch} onChange={(e) => setVilleSearch(e.target.value)}
              placeholder="Rechercher un concert / salle..." className="pl-9" data-testid="ville-modal-search" />
          </div>
          <div className="overflow-auto mt-3 space-y-1 pr-1">
            {villeList.filter((c) => {
              const q = villeSearch.toLowerCase();
              return !q || (c.concert || "").toLowerCase().includes(q) || (c.salle || "").toLowerCase().includes(q);
            }).map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm" data-testid={`ville-concert-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{c.concert}</div>
                  <div className="text-xs text-muted-foreground">
                    {c.day ? `${c.day} ` : ""}{MONTHS_FR[c.month]} {c.year} · {c.salle}
                  </div>
                </div>
                <span className="text-xs text-muted-foreground tabular-nums shrink-0">{c.jauge}</span>
              </div>
            ))}
            {villeList.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">Aucun concert</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
